import React, { useEffect, useState } from "react";
import moment from "moment";
import { Table, Button, Empty, Message, Modal, Input, Tag } from "@arco-design/web-react";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import { FetchApi, PostApi, DeleteApi } from "../../Api/AuthenticatedApi";

// The org's administrators = its members. Lists real members, lets you remove one
// (backend blocks removing yourself), and invites new admins by email.
export default function OrganizationAdmins() {
    const navigate = useNavigate();
    const { getAccessTokenSilently, user } = useAuth0();
    const [members, setMembers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [refresh, setRefresh] = useState(true);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState("");

    const authFail = () => navigate("/login");

    useEffect(() => {
        if (!refresh) return;
        FetchApi(getAccessTokenSilently, "/api/members/",
            (data) => setMembers(Array.isArray(data) ? data : []), authFail,
            () => Message.error("Failed to load administrators"));
        FetchApi(getAccessTokenSilently, "/api/invites/",
            (data) => { setInvites(Array.isArray(data) ? data : []); setRefresh(false); },
            authFail, () => setRefresh(false));
    }, [getAccessTokenSilently, navigate, refresh]);

    const remove = (u) => {
        Modal.confirm({
            title: "Remove administrator?",
            content: `Remove ${u.name || u.email} from this organization? They keep their account but lose access to this org.`,
            okButtonProps: { status: "danger" },
            okText: "Remove",
            onOk: () => {
                DeleteApi(getAccessTokenSilently, "/api/members/" + u.id,
                    () => { Message.success("Administrator removed"); setRefresh(true); }, authFail,
                    (err) => Message.error((err && err.message) || "Failed to remove administrator"), {});
            },
        });
    };

    const sendInvite = () => {
        const email = inviteEmail.trim();
        if (!email) return;
        PostApi(getAccessTokenSilently, "/api/invites/",
            () => { Message.success("Invitation sent to " + email); setInviteOpen(false); setInviteEmail(""); setRefresh(true); },
            authFail,
            (err) => Message.error((err && err.message) || "Failed to send invitation"),
            { email });
    };

    const columns = [
        { title: "Name", dataIndex: "name" },
        { title: "Email", dataIndex: "email" },
        {
            title: "Registered",
            dataIndex: "created_date",
            render: (d) => <div>{d ? moment(d).format("MMM D, YYYY") : ""}</div>,
        },
        {
            title: "",
            render: (_, u) => {
                const isSelf = user && user.email && u.email === user.email;
                return isSelf ? (
                    <span style={{ color: "var(--color-text-3)", fontSize: 12 }}>You</span>
                ) : (
                    <Button size="small" shape="circle" type="text" status="danger" icon={<IconDelete />} onClick={() => remove(u)} />
                );
            },
        },
    ];

    const pending = invites.filter((i) => i.status === "pending");

    return (
        <React.Fragment>
            <Table
                size="small"
                columns={columns}
                data={members}
                rowKey="id"
                pagination={false}
                border={false}
                noDataElement={<Empty description="No administrators yet" />}
            />

            {pending.length > 0 && (
                <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 8 }}>Pending invitations</div>
                    {pending.map((i) => (
                        <div key={i.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                            <Tag color="orange">Invited</Tag>
                            <span>{i.email}</span>
                        </div>
                    ))}
                </div>
            )}

            <div style={{ marginTop: 24 }}>
                <Button type="primary" icon={<IconPlus />} onClick={() => setInviteOpen(true)}>
                    Add admin
                </Button>
            </div>

            <Modal
                title="Invite an administrator"
                visible={inviteOpen}
                onOk={sendInvite}
                onCancel={() => setInviteOpen(false)}
                okText="Send invitation"
            >
                <p style={{ color: "var(--color-text-3)", marginTop: 0 }}>
                    We'll email them a link to sign up. When they log in with this email, they can join your organization.
                </p>
                <Input placeholder="teammate@example.com" value={inviteEmail} onChange={setInviteEmail} onPressEnter={sendInvite} />
            </Modal>
        </React.Fragment>
    );
}
